import { useEffect, useState } from 'react'
import useTranslation from 'next-translate/useTranslation'
import clsx from 'clsx'
import CloseIcon from '@ancon/wildcat-ui/shared/icons/close.svg'
import CompanyIcon from '@ancon/wildcat-ui/shared/icons/company-outline.svg'

import useAppSelector from '../../../store/hooks/useAppSelector'
import useAppStore from '../../../store/hooks/useAppStore'
import {
  clientContextCompanyUsersListSelector,
  clientContextIsCompanyUserSelector,
} from '../../clientContext/store/clientContextSelectors'
import DropdownIcon from '../../app/components/DropdownIcon'
import PopoverPanel from '../../app/components/popover/PopoverPanel'
import usePopover from '../../app/hooks/usePopover'
import sessionStorageUtils from '../../app/utils/sessionStorageUtils'
import useSwitchProfile from '../hooks/useSwitchProfile'
import useShowCompanyProfileNudge from '../hooks/useShowCompanyProfileNudge'

import ProfileSwitcherOptions from './ProfileSwitcherOptions'
import styles from './CompanyProfileNudge.module.scss'

type CompanyProfileNudgeProps = {
  /** Can be hidden for the rest of the browser session */
  dismissible?: boolean
  /** Called after switching to a company profile */
  onSwitched?: () => void
  className?: string
}

/**
 * Quiet hint for a private user who also has a company profile: "Ordering
 * for work?" with a switch. One company switches right away, several open
 * the profile list. Sits where company users see the company banner.
 */
export default function CompanyProfileNudge({
  dismissible = false,
  onSwitched,
  className,
}: CompanyProfileNudgeProps) {
  const { t } = useTranslation('common')
  const store = useAppStore()

  const isShown = useShowCompanyProfileNudge()
  const companyUsers = useAppSelector(clientContextCompanyUsersListSelector)
  const { switchToCompanyProfile, isSwitching } = useSwitchProfile()
  const { isOpen, close, containerRef, triggerRef, triggerProps, popoverId } =
    usePopover<HTMLSpanElement>()

  // Read after mount: session storage isn't there during server rendering
  const [isDismissed, setIsDismissed] = useState(dismissible)

  useEffect(() => {
    if (dismissible) {
      setIsDismissed(
        !!sessionStorageUtils.getItem<boolean>('companyNudgeDismissed'),
      )
    }
  }, [dismissible])

  if (!isShown || isDismissed || !companyUsers.length) {
    return null
  }

  const [companyUser] = companyUsers
  const hasSeveralCompanies = companyUsers.length > 1

  async function handleSwitch() {
    const isSwitched = await switchToCompanyProfile(
      companyUser.id,
      companyUser.emailVerified,
    )

    if (isSwitched) {
      onSwitched?.()
    }
  }

  // The profile list also closes when the current (private) profile is
  // picked, so only report a switch that happened
  function handleProfileListDone() {
    close()

    if (clientContextIsCompanyUserSelector(store.getState())) {
      onSwitched?.()
    }
  }

  function handleDismiss() {
    sessionStorageUtils.setItem('companyNudgeDismissed', true)
    setIsDismissed(true)
  }

  return (
    <section
      className={clsx(styles.nudge, { [styles.nudgeOpen]: isOpen }, className)}
      aria-label={t('companyNudge.title')}
    >
      <span className={styles.icon} aria-hidden>
        <CompanyIcon />
      </span>
      <span className={styles.text}>
        <strong>{t('companyNudge.title')}</strong>{' '}
        {hasSeveralCompanies
          ? t('companyNudge.descriptionSeveral')
          : t('companyNudge.description', {
              companyName: companyUser.companyName,
            })}
      </span>
      <span className={styles.actions}>
        {hasSeveralCompanies ? (
          <span className={styles.switcher} ref={containerRef}>
            <button
              type="button"
              ref={triggerRef}
              className={styles.button}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...triggerProps}
            >
              {t('companyNudge.chooseCompany')}
              <DropdownIcon isOpen={isOpen} />
            </button>
            {isOpen && (
              <PopoverPanel id={popoverId}>
                <ProfileSwitcherOptions
                  onDone={handleProfileListDone}
                  hideCompanyLinks
                />
              </PopoverPanel>
            )}
          </span>
        ) : (
          <button
            type="button"
            className={styles.button}
            disabled={isSwitching}
            onClick={handleSwitch}
          >
            {t('companyNudge.switchTo', {
              companyName: companyUser.companyName,
            })}
          </button>
        )}
        {dismissible && (
          <button
            type="button"
            className={styles.dismiss}
            aria-label={t('companyNudge.dismiss')}
            onClick={handleDismiss}
          >
            <CloseIcon aria-hidden />
          </button>
        )}
      </span>
    </section>
  )
}
